import { useEffect, useState } from 'react';
import i18n from '@/i18n';

// ─────────────────────────────────────────────────────────────
// Multilingue des mini-applications du compte utilisateur.
//
// Les applications personnelles (Notes, Tasks, Flashcards, Study AI,
// App Store, Espace d'études) suivent la langue choisie dans le compte.
// Un dictionnaire local évite de gonfler les fichiers i18n globaux, et le
// hook se re-rend automatiquement quand la langue du compte change.
// ─────────────────────────────────────────────────────────────

export type AppLang = 'fr' | 'en' | 'ar' | 'da';

type Dict = Record<string, string>;

const fr: Dict = {
  // Coque
  'app.back': "Retour à l'App Store",
  'app.offline': 'Hors ligne',
  'app.syncing': 'Synchro…',
  'app.pending': 'en attente',
  'app.synced': 'Synchronisé',
  'app.syncNow': 'Synchroniser',
  'app.close': 'Fermer',
  'app.cancel': 'Annuler',
  'app.create': 'Créer',
  'app.save': 'Enregistrer',
  'app.delete': 'Supprimer',
  'app.open': 'Ouvrir',
  'app.add': 'Ajouter',
  'app.search': 'Rechercher…',

  // Notes
  'notes.title': 'Zifek Notes',
  'notes.subtitle': 'Vos carnets de cours, organisés comme vous voulez.',
  'notes.notebooks': 'Mes carnets',
  'notes.closed': 'Carnets fermés',
  'notes.newNotebook': 'Nouveau carnet',
  'notes.name': 'Nom du carnet',
  'notes.subject': 'Matière / module',
  'notes.year': 'Année académique',
  'notes.period': 'Découpage',
  'notes.periodSemester': 'Semestre',
  'notes.periodTrimester': 'Trimestre',
  'notes.periodYear': 'Année complète',
  'notes.periodOther': 'Autre',
  'notes.periodLabel': 'Libellé du découpage',
  'notes.periodLabelHint': 'Ex. Semestre 2, Trimestre 3, Module 4…',
  'notes.emptyNotebooks': 'Aucun carnet pour le moment. Créez votre premier carnet.',
  'notes.emptyNotes': 'Aucune note dans ce carnet.',
  'notes.newNote': 'Nouvelle note',
  'notes.untitled': 'Sans titre',
  'notes.placeholder': 'Commencez à écrire votre cours…',
  'notes.selectNotebook': 'Choisissez un carnet pour voir ses notes.',
  'notes.selectNote': 'Sélectionnez une note pour la lire ou la modifier.',
  'notes.all': 'Toutes',
  'notes.favorites': 'Favoris',
  'notes.important': 'Importantes',
  'notes.toReview': 'À réviser',
  'notes.words': 'mots',
  'notes.autosave': 'Enregistrement automatique',
  'notes.closeNotebook': 'Fermer le carnet',
  'notes.reopenNotebook': 'Rouvrir le carnet',
  'notes.deleteNotebook': 'Supprimer le carnet',
  'notes.confirmDeleteNotebook': 'Supprimer ce carnet et toutes ses notes ?',
  'notes.confirmDeleteNote': 'Supprimer cette note ?',
  'notes.share': 'Partager',
  'notes.duplicate': 'Dupliquer',
  'notes.export': 'Exporter',
  'notes.versions': 'Versions',
  'notes.saveVersion': 'Enregistrer la version',
  'notes.restore': 'Restaurer',
  'notes.noVersions': 'Aucune version enregistrée.',
  'notes.askAi': "Interroger l'IA",
  'notes.summary': 'Résumé de révision',
  'notes.summaryEmpty': 'Ces notes ne contiennent pas encore assez de texte pour un résumé.',
  'notes.selection': 'Sélection IA',
  'notes.selected': 'sélectionnée(s)',
  'notes.makeQuiz': 'Créer un quiz',
  'notes.noteCount': 'note',
  'notes.notesCount': 'notes',

  // Tasks
  'tasks.title': 'Zifek Tasks & Planner',
  'tasks.subtitle': 'Vos devoirs, examens et révisions, sans stress.',
  'tasks.newTask': 'Nouvelle tâche',
  'tasks.newEvent': 'Événement',
  'tasks.tasks': 'Tâches',
  'tasks.day': 'Jour',
  'tasks.week': 'Semaine',
  'tasks.month': 'Mois',
  'tasks.today': "Aujourd'hui",
  'tasks.todo': 'À faire',
  'tasks.overdue': 'En retard',
  'tasks.done': 'Terminées',
  'tasks.events': 'Événements',
  'tasks.details': 'Détails (facultatif)',
  'tasks.priority': 'Priorité',
  'tasks.low': 'Basse',
  'tasks.normal': 'Normale',
  'tasks.high': 'Haute',
  'tasks.estimate': 'min estimées',
  'tasks.course': 'Cours',
  'tasks.exam': 'Examen',
  'tasks.appointment': 'Rendez-vous',
  'tasks.revision': 'Révision',
  'tasks.once': 'Une seule fois',
  'tasks.weekly': 'Chaque semaine',
  'tasks.noTasks': 'Aucune tâche pour le moment.',
  'tasks.upcoming': 'Échéances proches',
  'tasks.nothingSoon': 'Rien à venir. Profitez-en.',
  'tasks.noEvents': 'Aucun événement.',
  'tasks.reminders': 'Activer les rappels',
  'tasks.remindersOn': 'Rappels actifs',
  'tasks.emptyDay': 'Rien de prévu ce jour.',

  // Flashcards
  'cards.title': 'Zifek Flashcards & Quiz',
  'cards.subtitle': 'Révisez en jouant, gagnez de l’XP chaque jour.',
  'cards.newDeck': 'Nouveau jeu',
  'cards.decks': 'Jeux de cartes',
  'cards.cards': 'Cartes',
  'cards.due': 'À réviser',
  'cards.accuracy': 'Précision',
  'cards.quiz': 'Quiz',
  'cards.exam': 'Examen',
  'cards.cardsMode': 'Cartes',
  'cards.start': 'Commencer',
  'cards.question': 'Question',
  'cards.result': 'Résultat',
  'cards.restart': 'Recommencer',
  'cards.backToDeck': 'Retour au jeu',
  'cards.level': 'Niveau',
  'cards.xp': 'XP',
  'cards.streak': 'Série',
  'cards.hearts': 'Vies',
  'cards.correct': 'Bravo !',
  'cards.wrong': 'Presque…',
  'cards.generate': "Générer avec l'IA",
  'cards.generateTitle': 'Créer un quiz avec l’IA',
  'cards.sourceNotes': 'Depuis mes notes',
  'cards.sourceDocs': 'Depuis un document',
  'cards.pickDocument': 'Choisir un fichier (Word, Excel, PDF…)',
  'cards.generating': 'L’IA prépare vos questions…',
  'cards.generated': 'questions générées. Vérifiez-les avant de réviser.',
  'cards.flip': 'Cliquer pour révéler la réponse',
  'cards.emptyDeck': 'Ce jeu est vide. Ajoutez vos premières cartes.',
  'cards.deleteDeck': 'Supprimer le jeu',
  'cards.questionLabel': 'Question',
  'cards.answerLabel': 'Réponse',
  'cards.explanationLabel': 'Explication (facultatif)',
  'cards.addCard': 'Ajouter la carte',

  // Study AI
  'ai.title': 'Zifek Study AI',
  'ai.subtitle': 'Votre assistant de révision, avec ou sans sources.',
  'ai.newChat': 'Nouvelle conversation',
  'ai.sources': 'Sources',
  'ai.history': 'Conversations',
  'ai.noSources': 'Aucune source',
  'ai.noConversation': 'Aucune conversation',
  'ai.placeholder': 'Posez votre question…',
  'ai.placeholderSources': 'Interrogez vos sources…',
  'ai.send': 'Envoyer',
  'ai.references': 'Références',
  'ai.generalMode': 'Mode général : l’IA répond à tous les sujets.',
  'ai.studyMode': 'Mode étude : l’IA répond à partir de vos sources.',
  'ai.revoke': 'Retirer toutes les sources',
  'ai.empty': 'Commencez une conversation : choisissez des sources, ou laissez vide pour parler à l’IA comme à un assistant général.',
  'ai.noSourcesFound': 'Créez des notes ou importez un document pour interroger vos propres cours.',
  'ai.delete': 'Supprimer la conversation',
  'ai.analyse': 'Analyse en cours…',

  // Espace d'études
  'space.title': "Mon espace d'études",
  'space.subtitle': 'Tout ce qui compte aujourd’hui : révisions, échéances et quiz.',
  'space.toReview': 'Notes à réviser',
  'space.deadlines': 'Échéances proches',
  'space.quizToday': 'Quiz du jour',
  'space.noNotes': 'Aucune note marquée à réviser.',
  'space.noDeadlines': 'Aucune échéance proche.',
  'space.noQuiz': 'Aucun jeu de cartes pour le moment.',
  'space.openNotes': 'Ouvrir mes notes',
  'space.openTasks': 'Ouvrir le planning',
  'space.openCards': 'Ouvrir les quiz',
  'space.studyNow': 'Réviser maintenant',

  // App Store
  'store.title': 'App Store Zifek',
  'store.subtitle': 'Des outils personnels pour organiser vos études.',
  'store.installed': 'Mes applications',
  'store.available': 'disponibles',
  'store.seeMore': 'Voir plus',
  'store.install': 'Installer',
  'store.installPending': 'Installation…',
  'store.emptyInstalled': 'Aucune application installée pour le moment.',
  'store.pwaTitle': "Installer l'application",
  'store.pwaText': 'Vos notes et quiz restent utilisables hors connexion.',
  'store.pwaButton': 'Installer',
  'store.pwaLater': 'Plus tard',
  'store.pwaIos': 'Sur iPhone : Partager → Sur l’écran d’accueil.',
  'store.offlineReady': 'Fonctionne hors ligne',
};

const en: Dict = {
  'app.back': 'Back to App Store',
  'app.offline': 'Offline',
  'app.syncing': 'Syncing…',
  'app.pending': 'pending',
  'app.synced': 'Synced',
  'app.syncNow': 'Sync',
  'app.close': 'Close',
  'app.cancel': 'Cancel',
  'app.create': 'Create',
  'app.save': 'Save',
  'app.delete': 'Delete',
  'app.open': 'Open',
  'app.add': 'Add',
  'app.search': 'Search…',
  'notes.title': 'Zifek Notes',
  'notes.subtitle': 'Your course notebooks, organised your way.',
  'notes.notebooks': 'My notebooks',
  'notes.closed': 'Closed notebooks',
  'notes.newNotebook': 'New notebook',
  'notes.name': 'Notebook name',
  'notes.subject': 'Subject / module',
  'notes.year': 'Academic year',
  'notes.period': 'Period type',
  'notes.periodSemester': 'Semester',
  'notes.periodTrimester': 'Trimester',
  'notes.periodYear': 'Full year',
  'notes.periodOther': 'Other',
  'notes.periodLabel': 'Period label',
  'notes.periodLabelHint': 'e.g. Semester 2, Trimester 3, Module 4…',
  'notes.emptyNotebooks': 'No notebooks yet. Create your first one.',
  'notes.emptyNotes': 'No notes in this notebook.',
  'notes.newNote': 'New note',
  'notes.untitled': 'Untitled',
  'notes.placeholder': 'Start writing your lesson…',
  'notes.selectNotebook': 'Pick a notebook to see its notes.',
  'notes.selectNote': 'Select a note to read or edit it.',
  'notes.all': 'All',
  'notes.favorites': 'Favorites',
  'notes.important': 'Important',
  'notes.toReview': 'To review',
  'notes.words': 'words',
  'notes.autosave': 'Autosaved',
  'notes.closeNotebook': 'Close notebook',
  'notes.reopenNotebook': 'Reopen notebook',
  'notes.deleteNotebook': 'Delete notebook',
  'notes.confirmDeleteNotebook': 'Delete this notebook and all its notes?',
  'notes.confirmDeleteNote': 'Delete this note?',
  'notes.share': 'Share',
  'notes.duplicate': 'Duplicate',
  'notes.export': 'Export',
  'notes.versions': 'Versions',
  'notes.saveVersion': 'Save current version',
  'notes.restore': 'Restore',
  'notes.noVersions': 'No saved version yet.',
  'notes.askAi': 'Ask AI',
  'notes.summary': 'Revision summary',
  'notes.summaryEmpty': 'These notes do not contain enough text for a summary yet.',
  'notes.selection': 'AI selection',
  'notes.selected': 'selected',
  'notes.makeQuiz': 'Create a quiz',
  'notes.noteCount': 'note',
  'notes.notesCount': 'notes',
  'tasks.title': 'Zifek Tasks & Planner',
  'tasks.subtitle': 'Assignments, exams and revision, stress-free.',
  'tasks.newTask': 'New task',
  'tasks.newEvent': 'Event',
  'tasks.tasks': 'Tasks',
  'tasks.day': 'Day',
  'tasks.week': 'Week',
  'tasks.month': 'Month',
  'tasks.today': 'Today',
  'tasks.todo': 'To do',
  'tasks.overdue': 'Overdue',
  'tasks.done': 'Done',
  'tasks.events': 'Events',
  'tasks.details': 'Details (optional)',
  'tasks.priority': 'Priority',
  'tasks.low': 'Low',
  'tasks.normal': 'Normal',
  'tasks.high': 'High',
  'tasks.estimate': 'est. minutes',
  'tasks.course': 'Class',
  'tasks.exam': 'Exam',
  'tasks.appointment': 'Appointment',
  'tasks.revision': 'Revision',
  'tasks.once': 'Once',
  'tasks.weekly': 'Weekly',
  'tasks.noTasks': 'No task yet.',
  'tasks.upcoming': 'Upcoming deadlines',
  'tasks.nothingSoon': 'Nothing coming up. Enjoy.',
  'tasks.noEvents': 'No event.',
  'tasks.reminders': 'Enable reminders',
  'tasks.remindersOn': 'Reminders on',
  'tasks.emptyDay': 'Nothing planned today.',
  'cards.title': 'Zifek Flashcards & Quiz',
  'cards.subtitle': 'Revise by playing, earn XP every day.',
  'cards.newDeck': 'New deck',
  'cards.decks': 'Decks',
  'cards.cards': 'Cards',
  'cards.due': 'Due',
  'cards.accuracy': 'Accuracy',
  'cards.quiz': 'Quiz',
  'cards.exam': 'Exam',
  'cards.cardsMode': 'Cards',
  'cards.start': 'Start',
  'cards.question': 'Question',
  'cards.result': 'Result',
  'cards.restart': 'Play again',
  'cards.backToDeck': 'Back to deck',
  'cards.level': 'Level',
  'cards.xp': 'XP',
  'cards.streak': 'Streak',
  'cards.hearts': 'Hearts',
  'cards.correct': 'Nice!',
  'cards.wrong': 'Almost…',
  'cards.generate': 'Generate with AI',
  'cards.generateTitle': 'Create a quiz with AI',
  'cards.sourceNotes': 'From my notes',
  'cards.sourceDocs': 'From a document',
  'cards.pickDocument': 'Pick a file (Word, Excel, PDF…)',
  'cards.generating': 'AI is preparing your questions…',
  'cards.generated': 'questions generated. Review them before revising.',
  'cards.flip': 'Tap to reveal the answer',
  'cards.emptyDeck': 'This deck is empty. Add your first cards.',
  'cards.deleteDeck': 'Delete deck',
  'cards.questionLabel': 'Question',
  'cards.answerLabel': 'Answer',
  'cards.explanationLabel': 'Explanation (optional)',
  'cards.addCard': 'Add card',
  'ai.title': 'Zifek Study AI',
  'ai.subtitle': 'Your revision assistant, with or without sources.',
  'ai.newChat': 'New chat',
  'ai.sources': 'Sources',
  'ai.history': 'Chats',
  'ai.noSources': 'No source',
  'ai.noConversation': 'No conversation',
  'ai.placeholder': 'Ask your question…',
  'ai.placeholderSources': 'Ask your sources…',
  'ai.send': 'Send',
  'ai.references': 'References',
  'ai.generalMode': 'General mode: the AI answers any topic.',
  'ai.studyMode': 'Study mode: the AI answers from your sources.',
  'ai.revoke': 'Remove all sources',
  'ai.empty': 'Start a chat: pick sources, or leave empty to talk to a general AI assistant.',
  'ai.noSourcesFound': 'Create notes or import a document to question your own lessons.',
  'ai.delete': 'Delete chat',
  'ai.analyse': 'Analysing…',
  'space.title': 'My study space',
  'space.subtitle': 'Everything that matters today: revision, deadlines and quizzes.',
  'space.toReview': 'Notes to review',
  'space.deadlines': 'Upcoming deadlines',
  'space.quizToday': 'Quiz of the day',
  'space.noNotes': 'No note marked for review.',
  'space.noDeadlines': 'No upcoming deadline.',
  'space.noQuiz': 'No deck yet.',
  'space.openNotes': 'Open my notes',
  'space.openTasks': 'Open planner',
  'space.openCards': 'Open quizzes',
  'space.studyNow': 'Study now',
  'store.title': 'Zifek App Store',
  'store.subtitle': 'Personal tools to organise your studies.',
  'store.installed': 'My apps',
  'store.available': 'available',
  'store.seeMore': 'See more',
  'store.install': 'Install',
  'store.installPending': 'Installing…',
  'store.emptyInstalled': 'No app installed yet.',
  'store.pwaTitle': 'Install the app',
  'store.pwaText': 'Your notes and quizzes stay available offline.',
  'store.pwaButton': 'Install',
  'store.pwaLater': 'Later',
  'store.pwaIos': 'On iPhone: Share → Add to Home Screen.',
  'store.offlineReady': 'Works offline',
};

const ar: Dict = {
  'app.back': 'العودة إلى متجر التطبيقات',
  'app.offline': 'بدون اتصال',
  'app.syncing': 'جارٍ المزامنة…',
  'app.pending': 'في الانتظار',
  'app.synced': 'متزامن',
  'app.syncNow': 'مزامنة',
  'app.close': 'إغلاق',
  'app.cancel': 'إلغاء',
  'app.create': 'إنشاء',
  'app.save': 'حفظ',
  'app.delete': 'حذف',
  'app.open': 'فتح',
  'app.add': 'إضافة',
  'app.search': 'بحث…',
  'notes.title': 'Zifek Notes',
  'notes.subtitle': 'دفاترك الدراسية، منظمة كما تريد.',
  'notes.notebooks': 'دفاتري',
  'notes.closed': 'دفاتر مغلقة',
  'notes.newNotebook': 'دفتر جديد',
  'notes.name': 'اسم الدفتر',
  'notes.subject': 'المادة / الوحدة',
  'notes.year': 'السنة الدراسية',
  'notes.period': 'نوع التقسيم',
  'notes.periodSemester': 'سداسي',
  'notes.periodTrimester': 'ثلاثي',
  'notes.periodYear': 'سنة كاملة',
  'notes.periodOther': 'آخر',
  'notes.periodLabel': 'عنوان التقسيم',
  'notes.periodLabelHint': 'مثال: السداسي 2، الثلاثي 3…',
  'notes.emptyNotebooks': 'لا يوجد دفتر بعد. أنشئ دفاترك الأولى.',
  'notes.emptyNotes': 'لا توجد ملاحظات في هذا الدفتر.',
  'notes.newNote': 'ملاحظة جديدة',
  'notes.untitled': 'بدون عنوان',
  'notes.placeholder': 'ابدأ كتابة درسك…',
  'notes.selectNotebook': 'اختر دفتراً لعرض ملاحظاته.',
  'notes.selectNote': 'اختر ملاحظة لقراءتها أو تعديلها.',
  'notes.all': 'الكل',
  'notes.favorites': 'المفضلة',
  'notes.important': 'مهمة',
  'notes.toReview': 'للمراجعة',
  'notes.words': 'كلمة',
  'notes.autosave': 'حفظ تلقائي',
  'notes.closeNotebook': 'إغلاق الدفتر',
  'notes.reopenNotebook': 'إعادة فتح الدفتر',
  'notes.deleteNotebook': 'حذف الدفتر',
  'notes.confirmDeleteNotebook': 'حذف هذا الدفتر وكل ملاحظاته؟',
  'notes.confirmDeleteNote': 'حذف هذه الملاحظة؟',
  'notes.share': 'مشاركة',
  'notes.duplicate': 'نسخ',
  'notes.export': 'تصدير',
  'notes.versions': 'النسخ',
  'notes.saveVersion': 'حفظ النسخة الحالية',
  'notes.restore': 'استرجاع',
  'notes.noVersions': 'لا توجد نسخة محفوظة.',
  'notes.askAi': 'اسأل الذكاء الاصطناعي',
  'notes.summary': 'ملخص للمراجعة',
  'notes.summaryEmpty': 'لا تحتوي هذه الملاحظات على نص كافٍ لإنشاء ملخص.',
  'notes.selection': 'تحديد للذكاء الاصطناعي',
  'notes.selected': 'محددة',
  'notes.makeQuiz': 'إنشاء اختبار',
  'notes.noteCount': 'ملاحظة',
  'notes.notesCount': 'ملاحظات',
  'tasks.title': 'Zifek Tasks & Planner',
  'tasks.subtitle': 'واجباتك وامتحاناتك ومراجعاتك بلا توتر.',
  'tasks.newTask': 'مهمة جديدة',
  'tasks.newEvent': 'حدث',
  'tasks.tasks': 'المهام',
  'tasks.day': 'يوم',
  'tasks.week': 'أسبوع',
  'tasks.month': 'شهر',
  'tasks.today': 'اليوم',
  'tasks.todo': 'للإنجاز',
  'tasks.overdue': 'متأخرة',
  'tasks.done': 'منجزة',
  'tasks.events': 'الأحداث',
  'tasks.details': 'تفاصيل (اختياري)',
  'tasks.priority': 'الأولوية',
  'tasks.low': 'منخفضة',
  'tasks.normal': 'عادية',
  'tasks.high': 'عالية',
  'tasks.estimate': 'دقيقة تقديرية',
  'tasks.course': 'درس',
  'tasks.exam': 'امتحان',
  'tasks.appointment': 'موعد',
  'tasks.revision': 'مراجعة',
  'tasks.once': 'مرة واحدة',
  'tasks.weekly': 'كل أسبوع',
  'tasks.noTasks': 'لا توجد مهمة بعد.',
  'tasks.upcoming': 'مواعيد قريبة',
  'tasks.nothingSoon': 'لا شيء قادم. استمتع.',
  'tasks.noEvents': 'لا يوجد حدث.',
  'tasks.reminders': 'تفعيل التذكيرات',
  'tasks.remindersOn': 'التذكيرات مفعلة',
  'tasks.emptyDay': 'لا شيء مبرمج اليوم.',
  'cards.title': 'Zifek Flashcards & Quiz',
  'cards.subtitle': 'راجع باللعب واكسب نقاط XP كل يوم.',
  'cards.newDeck': 'مجموعة جديدة',
  'cards.decks': 'المجموعات',
  'cards.cards': 'البطاقات',
  'cards.due': 'للمراجعة',
  'cards.accuracy': 'الدقة',
  'cards.quiz': 'اختبار',
  'cards.exam': 'امتحان',
  'cards.cardsMode': 'بطاقات',
  'cards.start': 'ابدأ',
  'cards.question': 'سؤال',
  'cards.result': 'النتيجة',
  'cards.restart': 'إعادة',
  'cards.backToDeck': 'العودة إلى المجموعة',
  'cards.level': 'المستوى',
  'cards.xp': 'XP',
  'cards.streak': 'التتابع',
  'cards.hearts': 'القلوب',
  'cards.correct': 'ممتاز!',
  'cards.wrong': 'تقريباً…',
  'cards.generate': 'إنشاء بالذكاء الاصطناعي',
  'cards.generateTitle': 'إنشاء اختبار بالذكاء الاصطناعي',
  'cards.sourceNotes': 'من ملاحظاتي',
  'cards.sourceDocs': 'من مستند',
  'cards.pickDocument': 'اختر ملفاً (Word، Excel، PDF…)',
  'cards.generating': 'الذكاء الاصطناعي يحضّر الأسئلة…',
  'cards.generated': 'سؤالاً تم إنشاؤه. راجعها قبل المراجعة.',
  'cards.flip': 'اضغط لإظهار الجواب',
  'cards.emptyDeck': 'هذه المجموعة فارغة. أضف بطاقاتك الأولى.',
  'cards.deleteDeck': 'حذف المجموعة',
  'cards.questionLabel': 'السؤال',
  'cards.answerLabel': 'الجواب',
  'cards.explanationLabel': 'توضيح (اختياري)',
  'cards.addCard': 'إضافة البطاقة',
  'ai.title': 'Zifek Study AI',
  'ai.subtitle': 'مساعدك للمراجعة، بمصادر أو بدونها.',
  'ai.newChat': 'محادثة جديدة',
  'ai.sources': 'المصادر',
  'ai.history': 'المحادثات',
  'ai.noSources': 'بدون مصدر',
  'ai.noConversation': 'لا توجد محادثة',
  'ai.placeholder': 'اطرح سؤالك…',
  'ai.placeholderSources': 'اسأل مصادرك…',
  'ai.send': 'إرسال',
  'ai.references': 'المراجع',
  'ai.generalMode': 'الوضع العام: يجيب الذكاء الاصطناعي عن كل المواضيع.',
  'ai.studyMode': 'وضع الدراسة: يجيب انطلاقاً من مصادرك.',
  'ai.revoke': 'إزالة كل المصادر',
  'ai.empty': 'ابدأ محادثة: اختر مصادر، أو اتركها فارغة لتتحدث مع مساعد عام.',
  'ai.noSourcesFound': 'أنشئ ملاحظات أو استورد مستنداً لسؤال دروسك.',
  'ai.delete': 'حذف المحادثة',
  'ai.analyse': 'جارٍ التحليل…',
  'space.title': 'فضاء دراستي',
  'space.subtitle': 'كل ما يهم اليوم: المراجعة والمواعيد والاختبارات.',
  'space.toReview': 'ملاحظات للمراجعة',
  'space.deadlines': 'مواعيد قريبة',
  'space.quizToday': 'اختبار اليوم',
  'space.noNotes': 'لا توجد ملاحظة للمراجعة.',
  'space.noDeadlines': 'لا يوجد موعد قريب.',
  'space.noQuiz': 'لا توجد مجموعة بعد.',
  'space.openNotes': 'فتح ملاحظاتي',
  'space.openTasks': 'فتح المخطط',
  'space.openCards': 'فتح الاختبارات',
  'space.studyNow': 'راجع الآن',
  'store.title': 'متجر تطبيقات Zifek',
  'store.subtitle': 'أدوات شخصية لتنظيم دراستك.',
  'store.installed': 'تطبيقاتي',
  'store.available': 'متاحة',
  'store.seeMore': 'عرض المزيد',
  'store.install': 'تثبيت',
  'store.installPending': 'جارٍ التثبيت…',
  'store.emptyInstalled': 'لا يوجد تطبيق مثبت بعد.',
  'store.pwaTitle': 'تثبيت التطبيق',
  'store.pwaText': 'تبقى ملاحظاتك واختباراتك متاحة بدون اتصال.',
  'store.pwaButton': 'تثبيت',
  'store.pwaLater': 'لاحقاً',
  'store.pwaIos': 'على iPhone: مشاركة → إضافة إلى الشاشة الرئيسية.',
  'store.offlineReady': 'يعمل بدون اتصال',
};

const da: Dict = {
  'app.back': 'Tilbage til App Store',
  'app.offline': 'Offline',
  'app.syncing': 'Synkroniserer…',
  'app.pending': 'venter',
  'app.synced': 'Synkroniseret',
  'app.syncNow': 'Synkroniser',
  'app.close': 'Luk',
  'app.cancel': 'Annuller',
  'app.create': 'Opret',
  'app.save': 'Gem',
  'app.delete': 'Slet',
  'app.open': 'Åbn',
  'app.add': 'Tilføj',
  'app.search': 'Søg…',
  'notes.title': 'Zifek Notes',
  'notes.subtitle': 'Dine kursusnotesbøger, organiseret som du vil.',
  'notes.notebooks': 'Mine notesbøger',
  'notes.closed': 'Lukkede notesbøger',
  'notes.newNotebook': 'Ny notesbog',
  'notes.name': 'Notesbogens navn',
  'notes.subject': 'Fag / modul',
  'notes.year': 'Akademisk år',
  'notes.period': 'Periode',
  'notes.periodSemester': 'Semester',
  'notes.periodTrimester': 'Trimester',
  'notes.periodYear': 'Hele året',
  'notes.periodOther': 'Andet',
  'notes.periodLabel': 'Periodens navn',
  'notes.periodLabelHint': 'Fx Semester 2, Trimester 3…',
  'notes.emptyNotebooks': 'Ingen notesbog endnu. Opret din første.',
  'notes.emptyNotes': 'Ingen noter i denne notesbog.',
  'notes.newNote': 'Ny note',
  'notes.untitled': 'Uden titel',
  'notes.placeholder': 'Begynd at skrive din lektion…',
  'notes.selectNotebook': 'Vælg en notesbog for at se noterne.',
  'notes.selectNote': 'Vælg en note for at læse eller redigere.',
  'notes.all': 'Alle',
  'notes.favorites': 'Favoritter',
  'notes.important': 'Vigtige',
  'notes.toReview': 'Til repetition',
  'notes.words': 'ord',
  'notes.autosave': 'Gemmes automatisk',
  'notes.closeNotebook': 'Luk notesbog',
  'notes.reopenNotebook': 'Genåbn notesbog',
  'notes.deleteNotebook': 'Slet notesbog',
  'notes.confirmDeleteNotebook': 'Slet denne notesbog og alle noter?',
  'notes.confirmDeleteNote': 'Slet denne note?',
  'notes.share': 'Del',
  'notes.duplicate': 'Dupliker',
  'notes.export': 'Eksporter',
  'notes.versions': 'Versioner',
  'notes.saveVersion': 'Gem version',
  'notes.restore': 'Gendan',
  'notes.noVersions': 'Ingen gemt version.',
  'notes.askAi': 'Spørg AI',
  'notes.summary': 'Repetitionsresumé',
  'notes.summaryEmpty': 'Noterne har endnu ikke nok tekst til et resumé.',
  'notes.selection': 'AI-valg',
  'notes.selected': 'valgt',
  'notes.makeQuiz': 'Lav en quiz',
  'notes.noteCount': 'note',
  'notes.notesCount': 'noter',
  'tasks.title': 'Zifek Tasks & Planner',
  'tasks.subtitle': 'Opgaver, eksamener og repetition uden stress.',
  'tasks.newTask': 'Ny opgave',
  'tasks.newEvent': 'Begivenhed',
  'tasks.tasks': 'Opgaver',
  'tasks.day': 'Dag',
  'tasks.week': 'Uge',
  'tasks.month': 'Måned',
  'tasks.today': 'I dag',
  'tasks.todo': 'At gøre',
  'tasks.overdue': 'Forsinket',
  'tasks.done': 'Fuldført',
  'tasks.events': 'Begivenheder',
  'tasks.details': 'Detaljer (valgfrit)',
  'tasks.priority': 'Prioritet',
  'tasks.low': 'Lav',
  'tasks.normal': 'Normal',
  'tasks.high': 'Høj',
  'tasks.estimate': 'min. anslået',
  'tasks.course': 'Undervisning',
  'tasks.exam': 'Eksamen',
  'tasks.appointment': 'Aftale',
  'tasks.revision': 'Repetition',
  'tasks.once': 'Én gang',
  'tasks.weekly': 'Hver uge',
  'tasks.noTasks': 'Ingen opgave endnu.',
  'tasks.upcoming': 'Kommende deadlines',
  'tasks.nothingSoon': 'Intet i vente. Nyd det.',
  'tasks.noEvents': 'Ingen begivenhed.',
  'tasks.reminders': 'Aktivér påmindelser',
  'tasks.remindersOn': 'Påmindelser aktive',
  'tasks.emptyDay': 'Intet planlagt i dag.',
  'cards.title': 'Zifek Flashcards & Quiz',
  'cards.subtitle': 'Repeter gennem leg, optjen XP hver dag.',
  'cards.newDeck': 'Nyt sæt',
  'cards.decks': 'Sæt',
  'cards.cards': 'Kort',
  'cards.due': 'Til repetition',
  'cards.accuracy': 'Præcision',
  'cards.quiz': 'Quiz',
  'cards.exam': 'Eksamen',
  'cards.cardsMode': 'Kort',
  'cards.start': 'Start',
  'cards.question': 'Spørgsmål',
  'cards.result': 'Resultat',
  'cards.restart': 'Prøv igen',
  'cards.backToDeck': 'Tilbage til sæt',
  'cards.level': 'Niveau',
  'cards.xp': 'XP',
  'cards.streak': 'Serie',
  'cards.hearts': 'Liv',
  'cards.correct': 'Flot!',
  'cards.wrong': 'Næsten…',
  'cards.generate': 'Generer med AI',
  'cards.generateTitle': 'Lav en quiz med AI',
  'cards.sourceNotes': 'Fra mine noter',
  'cards.sourceDocs': 'Fra et dokument',
  'cards.pickDocument': 'Vælg en fil (Word, Excel, PDF…)',
  'cards.generating': 'AI forbereder dine spørgsmål…',
  'cards.generated': 'spørgsmål genereret. Gennemgå dem.',
  'cards.flip': 'Tryk for at se svaret',
  'cards.emptyDeck': 'Sættet er tomt. Tilføj dine første kort.',
  'cards.deleteDeck': 'Slet sæt',
  'cards.questionLabel': 'Spørgsmål',
  'cards.answerLabel': 'Svar',
  'cards.explanationLabel': 'Forklaring (valgfrit)',
  'cards.addCard': 'Tilføj kort',
  'ai.title': 'Zifek Study AI',
  'ai.subtitle': 'Din repetitionsassistent, med eller uden kilder.',
  'ai.newChat': 'Ny samtale',
  'ai.sources': 'Kilder',
  'ai.history': 'Samtaler',
  'ai.noSources': 'Ingen kilde',
  'ai.noConversation': 'Ingen samtale',
  'ai.placeholder': 'Stil dit spørgsmål…',
  'ai.placeholderSources': 'Spørg dine kilder…',
  'ai.send': 'Send',
  'ai.references': 'Referencer',
  'ai.generalMode': 'Generel tilstand: AI svarer på alle emner.',
  'ai.studyMode': 'Studietilstand: AI svarer ud fra dine kilder.',
  'ai.revoke': 'Fjern alle kilder',
  'ai.empty': 'Start en samtale: vælg kilder, eller lad det være tomt for en generel AI-assistent.',
  'ai.noSourcesFound': 'Opret noter eller importér et dokument for at spørge dine lektioner.',
  'ai.delete': 'Slet samtale',
  'ai.analyse': 'Analyserer…',
  'space.title': 'Mit studierum',
  'space.subtitle': 'Alt vigtigt i dag: repetition, deadlines og quizzer.',
  'space.toReview': 'Noter til repetition',
  'space.deadlines': 'Kommende deadlines',
  'space.quizToday': 'Dagens quiz',
  'space.noNotes': 'Ingen note markeret til repetition.',
  'space.noDeadlines': 'Ingen kommende deadline.',
  'space.noQuiz': 'Intet sæt endnu.',
  'space.openNotes': 'Åbn mine noter',
  'space.openTasks': 'Åbn planlægning',
  'space.openCards': 'Åbn quizzer',
  'space.studyNow': 'Repeter nu',
  'store.title': 'Zifek App Store',
  'store.subtitle': 'Personlige værktøjer til at organisere dine studier.',
  'store.installed': 'Mine apps',
  'store.available': 'tilgængelige',
  'store.seeMore': 'Se mere',
  'store.install': 'Installer',
  'store.installPending': 'Installerer…',
  'store.emptyInstalled': 'Ingen app installeret endnu.',
  'store.pwaTitle': 'Installer appen',
  'store.pwaText': 'Dine noter og quizzer virker offline.',
  'store.pwaButton': 'Installer',
  'store.pwaLater': 'Senere',
  'store.pwaIos': 'På iPhone: Del → Føj til hjemmeskærm.',
  'store.offlineReady': 'Virker offline',
};

const DICTS: Record<AppLang, Dict> = { fr, en, ar, da };

function normalizeLang(lang?: string): AppLang {
  const value = (lang || '').slice(0, 2).toLowerCase();
  if (value === 'en' || value === 'ar' || value === 'da') return value;
  return 'fr';
}

export function translateApp(lang: string | undefined, key: string): string {
  const dict = DICTS[normalizeLang(lang)];
  return dict[key] || fr[key] || key;
}

/** Hook de traduction des mini-apps : suit la langue du compte en direct. */
export function useAppsT() {
  const [lang, setLang] = useState<string>(() => {
    try {
      return localStorage.getItem('zifek_lang') || i18n.language || 'fr';
    } catch {
      return i18n.language || 'fr';
    }
  });

  useEffect(() => {
    const handler = (next: string) => setLang(next);
    i18n.on('languageChanged', handler);
    return () => {
      i18n.off('languageChanged', handler);
    };
  }, []);

  const resolved = normalizeLang(lang);
  const t = (key: string) => translateApp(resolved, key);
  return { t, lang: resolved, isRtl: resolved === 'ar' };
}

export function countLabel(count: number, singular: string, plural: string): string {
  return count === 1 ? singular : plural;
}